import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import TNTable from "@/components/TNTable";
import { CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslationContext } from "@/context/TranslationContext";
import { useListSmsTemplate, useSmsTemplateDelete } from "@/hooks/useSmsTemplate";
import { faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import "react-confirm-alert/src/react-confirm-alert.css";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import { smsTemplate } from "@/constants/translation/smsTemplate.translation";
import _ from "lodash";
import { setFormatDate } from "@/utils/dateFormate";
import ReadMore from "@/components/ReadMore";
import constants from "@/libs/constant";

/**
 * ListSmsTemplatePage Component
 *
 * This component is responsible for rendering and managing the list of SMS templates.
 * It includes functionalities such as searching, pagination, editing, and deleting SMS templates.
 * React component for adding a new SMS template.
 *
 * This component provides a form for adding SMS templates, including fields for different languages.
 * It fetches the list of languages and validates the form inputs before submitting the data.
 *
 * @returns {JSX.Element} React component for listing SMS templates.
 */
const ListSmsTemplatePage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  // Set all permissions to true for now (as requested)
  const canView = true;
  const canAddSmsTemplate = true;
  const canUpdateSmsTemplate = true;
  const canDeleteSmsTemplate = true;

  /**
   * setting state values to default,
   */
  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");

  // Clear localStorage and reset state when component unmounts
  useEffect(() => {
    return () => {
      // Clear localStorage
      localStorage.removeItem(constants.storageKeys.adminSmsTemplateTable);
      
      // Clear timeouts
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const [tempSearchText, setTempSearchText] = useState("");
  const searchTimeout = useRef(null);
  /**
   * This function will run when user enter something in search input
   */

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setSearchText(searchValue);
      setCurrentPage(1);
    }, 400); // Delay before updating real searchText
  };

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);


  const {
    refetch,
    data: listData,
    isLoading,
  } = useListSmsTemplate({
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && searchText.trim() ? { search_text: searchText.trim() } : {}),
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * This will clear all state values, and localstorage as well
   */
  const handleClearFilter = () => {
    setSearchText("");
    setCurrentPage(constants.pagination.defaultPage);
    setTempSearchText("");
  };
  const [paginationData, setPaginationData] = useState(null);
  /**
   * !This Function will call when user clicks on Edit Button
   * @param {*} tData which is current element of button
   */
  const handleEditClick = (tData) => {
    let sms_template_id =
      tData.currentTarget.getAttribute("sms_template_id");
    navigate(masters.smsTemplate.edit.replace(":id", sms_template_id));
  };
  /**
   * This will call an API to delete SMS template
   */
  const { mutate: doDeleteSmsTemplate } = useSmsTemplateDelete(
    (response) => {
      toast.success(response.message || t(messages.toast.smsTemplate.deleted));
      refetch();
    }
  );

  /**
   * This function will call on click of delete button, and will display alert,
   * and will call status update API after confirmation
   */
  const handleDeleteClick = (tData) => {
    let sms_template_id =
      tData.currentTarget.getAttribute("sms_template_id");
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-box">
            <div className="alert-popup">
            <FontAwesomeIcon
                className="alert-close"
                icon={faClose}
                onClick={() => {
                  onClose();
                }}
              />
              <h2 className="alert-title">
                {t(smsTemplate.list.delete_popup_heading_text)}
              </h2>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={() => {
                    doDeleteSmsTemplate(sms_template_id);
                    onClose();
                  }}
                >
                  {t(smsTemplate.list.delete_popup_yes_btn_text)}
                </CButton>
                <CButton
                  className="table-primary-button"
                  color="danger"
                  onClick={onClose}
                >
                  {t(smsTemplate.list.delete_popup_no_btn_text)}
                </CButton>
              </div>
            </div>
          </div>
        );
      },
    });
  };
  /**
   * !This Block is making Headers for the column
   * @param Not Required
   */

  const columnsjson = [
    {
      id: "created_at", // Add an explicit ID
      accessorKey: "createdAt", // Use createdAt from API
      header: t(smsTemplate.list.table.created_at_table_col_heading),
      cell: ({ getValue }) => <span>{setFormatDate(getValue())}</span>, //  Use getValue() in v8
    },
    {
      id: "title", //  Add an explicit ID
      accessorKey: "id",
      header: t(smsTemplate.list.table.title_table_col_heading),
      cell: ({ row }) => {
        const { templates } = row.original;

        if (_.isEmpty(templates)) return <div>—</div>; // Handle empty data
        return (
          <div>
            {_.map(templates, (template) => (
              <div key={template.id} className="d-flex gap-1">
                <strong>{`${template.language?.name || "N/A"}: `}</strong>
                <ReadMore>{template.title || "N/A"}</ReadMore>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: "action_2",
      accessorKey: "id",
      header: t(smsTemplate.list.table.action_table_col_heading),
      cell: ({ row }) => {
        return (
          <div className="d-flex gap-2 justify-content-center">
            {canUpdateSmsTemplate && (
              <div
                className="table-action-text"
                sms_template_id={row.original.uuid}
                onClick={handleEditClick}
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  className="list-icon"
                />
              </div>
            )}
            {canDeleteSmsTemplate && (
              <div
                className="table-action-text"
                sms_template_id={row.original.uuid}
                onClick={handleDeleteClick}
              >
                <FontAwesomeIcon 
                  icon={faTrash}
                  className="list-icon"
                />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const [smsTemplateList, setSmsTemplateList] = useState([]);
  /**
   * !This API will call while Page Load and set data. Once data load we are updating State
   */

  useEffect(() => {
    if (listData && !isLoading) {
      setSmsTemplateList(listData.data.smsTemplatesList || []);
      setPaginationData(listData.data.pagination);
    }
  }, [listData, isLoading]);

  const columns = useMemo(() => columnsjson, []);
  // if (paginationData === null) {
  //   return null;
  // }
  /**
   * This function will set current page of table
   */
  const pageIndexHandle = (pageIndex) => {
    setCurrentPage(pageIndex + 1);
  };
  /**
   * This function will call on Add SMS Template button Click and will take user to the add page of SMS Templates
   */
  const addSmsTemplateHandle = () => {
    navigate(masters.smsTemplate.add);
  };

  /**
   * Breadcum Lebel and Link
   */

  // Check if user has view permission
  if (!canView) {
    return (
      <div className="text-center p-4">
        <p className="text-danger">{t(smsTemplate.list.no_permission_to_view)}</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <CRow className="list-align">
          <CCol lg={10} md={10} sm={12} xs={12} className="d-flex gap-2 justify-content-end flex-wrap">
            <CFormInput
              placeholder={t(smsTemplate.list.search_placeholder)}
              type={constants.inputTypes.text}
              name={constants.formFields.smsTemplate.searchText}
              value={tempSearchText}
              onChange={handleSearch}
              className="filter-field-space form-control-sm"
              style={{ maxWidth: '300px' }}
            />
            <CButton 
              color="primary" 
              type="button" 
              className="filter-field-space" 
              onClick={handleClearFilter}
              style={{ display: tempSearchText.trim() ? 'inline-block' : 'none' }}
            >
              {t(smsTemplate.list.clear_filter_btn_text)}
            </CButton>
            {canAddSmsTemplate && (
              <CButton 
                color="primary" 
                type="button" 
                className="filter-field-space" 
                onClick={addSmsTemplateHandle}
              >
                {t(smsTemplate.list.create_sms_template_btn_text)}
              </CButton>
            )}
          </CCol>
        </CRow>
      </div>
      <TNTable
        columns={columns}
        data={smsTemplateList}
        paginationData={paginationData}
        onSelectPage={pageIndexHandle}
        t={t}
        pageIndexGet={currentPage - 1}
        key={Math.floor(Math.random() * (1000 - 1 + 1) + 1)}
        isLoading={isLoading}
        className="text-start table-sortable mt-3"
      />
    </>
  );
};
ListSmsTemplatePage.propTypes = {
  columns: PropTypes.any,
  row: PropTypes.any,
  data: PropTypes.any,
  paginationData: PropTypes.any,
  t: PropTypes.func,
  value: PropTypes.any,
};

// Static data that would have been passed via getStaticProps
ListSmsTemplatePage.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.smsTemplate?.list,
    pathname: masters.smsTemplate.list,
    active: true,
  },
];

export default ListSmsTemplatePage;
